import React, { useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import { Button } from 'primereact/button';
import { GridDefaultCellTemplates } from '../../../components/components/Grid/grid-template';
import { Select } from '../../../components/components/Select';
import { SelectOption } from '../../../shared/components/input';
import { EMPTY_GUID } from '../Model/CompanyCommissionRate';
import { createEmptyRetentionRate } from '../Model/CompanyRetentionRate';
import { LobPolicyTypeMapping } from '../Model/LobPolicyTypeMapping';

const hasValue = (value: any) => value !== null && value !== undefined && value !== '';

// Carrier ids arrive both dashed and undashed; compare and save as uppercase without dashes.
const toKey = (id: any) => String(id).trim().replace(/[{}-]/g, '').toUpperCase();

export const toIdList = (value: any, normalize = false): string[] =>
    Array.isArray(value) ? value.filter(hasValue).map(id => normalize ? toKey(id) : String(id)) : [];

const matchOptions = (value: any, options: SelectOption[]): SelectOption[] => {
    const keys = toIdList(value, true);
    return options.filter(option => keys.includes(toKey(option.value)));
};

export default class RetentionArrayService {
    private stateOptions: SelectOption[] = [];
    private lineOfBusinessOptions: SelectOption[] = [];
    private policyTypeOptions: SelectOption[] = [];
    private cancellationReasonOptions: SelectOption[] = [];

    private RetentionEditRowColumn = {
        CellTemplate: (cell: any) => {
            const params = cell.column.CellEditTemplateParameters;
            const rowLocked = cell?.row?.original?.isGridInEditMode;
            return (
                <div className="commission-action-btns">
                    <Button icon="bi bi-pencil" tooltip="Edit" rounded text size="small"
                        className="commission-action-btn" disabled={rowLocked || params?.disableEdit}
                        onClick={() => params?.cellClickHandler(cell.row.original, cell.column, 'edit_row')} />
                    <Button icon="bi bi-trash" tooltip="Delete" rounded text size="small" severity="danger"
                        className="commission-action-btn" disabled={rowLocked || params?.disableDelete}
                        onClick={() => params?.cellClickHandler(cell.row.original, cell.column, 'delete_row')} />
                </div>
            );
        }
    };

    private SelectCell = (cell: any) => {
        const editingRow = cell.row.original.editingRow;
        const params = cell.column.CellEditTemplateParameters;
        const options = params.getOptions ? params.getOptions(editingRow) : params.options;
        const cellDynamicParameters = cell.column.CellEditTemplateErrorHandler
            ? cell.column.CellEditTemplateErrorHandler(cell)
            : {};
        const [selectedValue, setSelectedValue] = useState(editingRow[cell.column.fieldName]);
        useEffect(() => {
            setSelectedValue(editingRow[cell.column.fieldName]);
        }, [editingRow[cell.column.fieldName]]);
        const normalizedSelectedValue = String(selectedValue || '').trim().toLowerCase();
        // When nothing is selected yet, skip matching entirely - an empty lookupCode/value would
        // otherwise equal '' and make the first option in the list look pre-selected.
        const selectedOption = normalizedSelectedValue
            ? (options || []).find((option: SelectOption) =>
                String(option.value).trim().toLowerCase() === normalizedSelectedValue ||
                option.label.trim().toLowerCase() === normalizedSelectedValue ||
                String(option.originalData?.lookupCode || '').trim().toLowerCase() === normalizedSelectedValue
            )
            : undefined;
        useEffect(() => {
            if (selectedOption && editingRow[cell.column.fieldName] !== selectedOption.value) {
                editingRow[cell.column.fieldName] = selectedOption.value;
                setSelectedValue(selectedOption.value);
            }
        }, [selectedOption?.value, editingRow, cell.column.fieldName]);
        return <Select
            key={`retention${editingRow.id}${cell.column.id}${cell.column.fieldName}${cell.column.fieldName === 'policyType' ? editingRow.lineOfBusiness : ''}`}
            id={`retention${editingRow.id}${cell.column.id}`}
            label="Please Select"
            placeholder="Please Select"
            showAsFloatingLabel={false}
            options={options || []}
            disabled={params.disabled ? params.disabled(editingRow) : false}
            defaultSelected={selectedOption ? [selectedOption] : []}
            onChange={(values: any[]) => {
                const value = values.length > 0 ? values[0] : '';
                setSelectedValue(value);
                params.cellDataChangeHandler(editingRow, cell.column, value, null);
            }}
            {...cellDynamicParameters}
        />;
    };

    private MultiSelectCell = (cell: any) => {
        const editingRow = cell.row.original.editingRow;
        const params = cell.column.CellEditTemplateParameters;
        const fieldName = cell.column.fieldName;
        const options: SelectOption[] = params.options || [];
        const cellDynamicParameters = cell.column.CellEditTemplateErrorHandler
            ? cell.column.CellEditTemplateErrorHandler(cell)
            : {};
        const [selectedValues, setSelectedValues] = useState<string[]>(toIdList(editingRow[fieldName]));
        useEffect(() => {
            setSelectedValues(toIdList(editingRow[fieldName]));
        }, [editingRow[fieldName]]);
        const selectedOptions = matchOptions(selectedValues, options);
        return <Select
            id={`retention${editingRow.id}${cell.column.id}`}
            showAsFloatingLabel={false}
            options={options}
            multiple={true}
            disabled={params.disabled ? params.disabled(editingRow) : false}
            defaultSelected={selectedOptions}
            inputProps={{ title: selectedOptions.map(option => option.label).join(', ') }}
            onChange={(values: any[]) => {
                const nextValues = values.map(String);
                setSelectedValues(nextValues);
                params.cellDataChangeHandler(editingRow, cell.column, nextValues, null);
            }}
            {...cellDynamicParameters}
        />;
    };

    private NumberCell = (cell: any) => {
        const editingRow = cell.row.original.editingRow;
        const params = cell.column.CellEditTemplateParameters;
        const fieldName = cell.column.fieldName;
        const [value, setValue] = useState(editingRow[fieldName] ?? '');
        const disabled = params.disabled(editingRow);
        useEffect(() => setValue(editingRow[fieldName] ?? ''), [editingRow[fieldName]]);
        return <Form.Control
            type="number"
            inputMode={fieldName === 'dollar' ? 'numeric' : 'decimal'}
            value={value}
            min={params.min}
            max={params.max}
            step={params.step}
            disabled={disabled}
            isInvalid={Boolean(editingRow.errors?.[fieldName])}
            title={editingRow.errors?.[fieldName]?.warningText}
            onChange={event => {
                const nextValue = event.currentTarget.value;
                if (nextValue === '') {
                    setValue('');
                    params.cellDataChangeHandler(editingRow, cell.column, '', null);
                    return;
                }
                const validFormat = fieldName === 'percentage'
                    ? /^\d{1,2}(\.\d?)?$/.test(nextValue)
                    : /^\d{1,4}$/.test(nextValue);
                if (!validFormat) return;
                const numericValue = Number(nextValue);
                const boundedValue = Math.min(params.max, Math.max(params.min, numericValue));
                const formattedValue = fieldName === 'dollar'
                    ? String(Math.trunc(boundedValue))
                    : String(boundedValue);
                setValue(formattedValue);
                params.cellDataChangeHandler(editingRow, cell.column, formattedValue, null);
            }}
            onBlur={() => {
                if (fieldName === 'percentage' && hasValue(editingRow[fieldName])) {
                    const formattedValue = Number(editingRow[fieldName]).toFixed(1);
                    setValue(formattedValue);
                    params.cellDataChangeHandler(editingRow, cell.column, formattedValue, null);
                }
            }}
        />;
    };

    public getGridColumns = (
        onEditingCellDataChange: (row: any, column: any, value: any, additionalInfo: any) => void,
        cellClickHandler: (row: any, column: any, additionalData: any) => void,
        stateOptions: SelectOption[],
        lineOfBusinessOptions: SelectOption[],
        policyTypeOptions: SelectOption[],
        carrierOptions: SelectOption[],
        disableEdit = false,
        disableDelete = false,
        includeRateFields = true,
        lobPolicyTypeMappings: LobPolicyTypeMapping[] = [],
        cancellationReasonOptions?: SelectOption[]
    ): any[] => {
        this.stateOptions = stateOptions;
        this.lineOfBusinessOptions = lineOfBusinessOptions;
        this.policyTypeOptions = policyTypeOptions;
        this.cancellationReasonOptions = cancellationReasonOptions || [];
        const includeCancellationReason = Boolean(cancellationReasonOptions);
        const carrierColumnWidth = includeRateFields || includeCancellationReason ? 317 : 577;
        const columns = [
        {
            Header: 'Action', accessor: 'enableGridRowEditing', hideColumnFilter: true,
            headerClassName: 'sticky-col sticky-col-header', cellClassName: 'sticky-col', width: 90,
            Cell: (cell: any) => cell.row?.original?.enableGridRowEditing
                ? GridDefaultCellTemplates.SaveRowGridColumn.CellTemplate(cell)
                : this.RetentionEditRowColumn.CellTemplate(cell),
            CellEditTemplateParameters: { cellClickHandler, disableEdit, disableDelete },
            CellEditTDProperties: { className: 'grd-edit-cell-td sticky-col' }
        },
        this.selectColumn('State', 'stateDisplay', 'state', stateOptions, onEditingCellDataChange, 120),
        this.selectColumn('Line of Business', 'lineOfBusinessDisplay', 'lineOfBusiness', this.lineOfBusinessOptions, onEditingCellDataChange, 180),
        this.selectColumn('Policy Type', 'policyTypeDisplay', 'policyType', this.policyTypeOptions, onEditingCellDataChange, 150,
            (row: any) => this.getPolicyTypeOptions(row.lineOfBusiness, lobPolicyTypeMappings),
            (row: any) => !hasValue(row.lineOfBusiness)),
        this.selectColumn('A+ Carrier', 'APlusCarrierToRateDisplay', 'APlusCarrierToRate', carrierOptions, onEditingCellDataChange, carrierColumnWidth,
            undefined, undefined, true)
        ];
        if (includeCancellationReason) {
            columns.push(this.selectColumn('Cancellation Reason', 'cancellationReasonDisplay', 'cancellationReason',
                this.cancellationReasonOptions, onEditingCellDataChange, 260, undefined, undefined, true));
        }
        if (includeRateFields) {
            columns.splice(4, 0,
                this.numberColumn('Percentage', 'percentageDisplay', 'percentage', 1, 99.9, 0.1, onEditingCellDataChange,
                    (row: any) => hasValue(row.dollar)),
                this.numberColumn('Dollar', 'dollarDisplay', 'dollar', 1, 9999, 1, onEditingCellDataChange,
                    (row: any) => hasValue(row.percentage)));
        }
        return columns;
    };

    private getPolicyTypeOptions = (lobId: any, mappings: LobPolicyTypeMapping[]): SelectOption[] => {
        const normalizedLobId = String(lobId || '').trim().toLowerCase();
        if (!normalizedLobId) return [];
        const policyTypes = new Map<string, SelectOption>();
        mappings.forEach(mapping => {
            if (String(mapping.lobId || '').trim().toLowerCase() !== normalizedLobId ||
                !mapping.policyTypeId || !mapping.policyType) return;
            const key = mapping.policyTypeId.toLowerCase();
            if (!policyTypes.has(key)) {
                policyTypes.set(key, { label: mapping.policyType, value: mapping.policyTypeId });
            }
        });
        return Array.from(policyTypes.values())
            .sort((left, right) => left.label.localeCompare(right.label));
    };

    private selectColumn = (header: string, accessor: string, fieldName: string, options: SelectOption[], handler: any,
        width: number, getOptions?: (row: any) => SelectOption[], disabled?: (row: any) => boolean, multiple = false): any => ({
        Header: header, accessor, fieldName, width,
        Cell: GridDefaultCellTemplates.EditableGridColumn.CellTemplate,
        CellViewTemplate: (cell: any) => {
            const displayValue = cell.row?.original?.[accessor] || '';
            return <span title={displayValue} aria-label={displayValue}
                style={{ display: 'block', maxWidth: width, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {displayValue}
            </span>;
        },
        CellEditTemplate: multiple ? this.MultiSelectCell : this.SelectCell,
        CellEditTemplateParameters: { options, getOptions, disabled, cellDataChangeHandler: handler },
        CellEditTemplateErrorHandler: GridDefaultCellTemplates.SelectFieldGridColumn.ErrorHandler,
        CellEditTDProperties: { className: 'grd-edit-cell-td' }
    });

    private numberColumn = (header: string, accessor: string, fieldName: string, min: number, max: number, step: number,
        handler: any, disabled: (row: any) => boolean): any => ({
        Header: header, accessor, fieldName, width: 130,
        Cell: GridDefaultCellTemplates.EditableGridColumn.CellTemplate,
        CellViewTemplate: (cell: any) => <span title={cell.row?.original?.[accessor]}>{cell.row?.original?.[accessor]}</span>,
        CellEditTemplate: this.NumberCell,
        CellEditTemplateParameters: { min, max, step, disabled, cellDataChangeHandler: handler },
        CellEditTemplateErrorHandler: GridDefaultCellTemplates.TextInputGridColumn.ErrorHandler,
        CellEditTDProperties: { className: 'grd-edit-cell-td' }
    });

    public getNewRow = (companyId: string, parentAgency: string): any => {
        const row: any = createEmptyRetentionRate(companyId, parentAgency);
        return { ...row, enableGridRowEditing: true, isGridInEditMode: false, editingRow: { ...row } };
    };

    public enrichDisplayFields = (row: any, carriers: any[], lineOfBusinesses: SelectOption[], policyTypes: SelectOption[]): void => {
        row.stateDisplay = this.stateOptions.find(option => option.value === row.state)?.label || '';
        const lineOfBusinessValue = String(row.lineOfBusiness || '').trim().toLowerCase();
        // Skip matching when unset - an empty lookupCode would otherwise equal '' and match the first option.
        const lineOfBusiness = lineOfBusinessValue
            ? lineOfBusinesses.find(option =>
                String(option.value).trim().toLowerCase() === lineOfBusinessValue ||
                option.label.trim().toLowerCase() === lineOfBusinessValue ||
                String(option.originalData?.lookupCode || '').trim().toLowerCase() === lineOfBusinessValue
            )
            : undefined;
        row.lineOfBusinessDisplay = lineOfBusiness?.label || '';
        row.policyTypeDisplay = policyTypes.find(option => option.value === row.policyType)?.label || '';
        row.percentageDisplay = hasValue(row.percentage) ? Number(row.percentage).toFixed(1) : '';
        row.dollarDisplay = hasValue(row.dollar) ? String(row.dollar) : '';
        row.APlusCarrierToRateDisplay = matchOptions(row.APlusCarrierToRate, carriers).map(option => option.label).join(', ');
        row.cancellationReasonDisplay = matchOptions(row.cancellationReason, this.cancellationReasonOptions).map(option => option.label).join(', ');
    };

    public validateRow = (originalRow: any, requireRate = true, requireCancellationReason = false): any => {
        const errors: any = {};
        const row = originalRow.editingRow;
        if (!row) return errors;
        if (!row.state) errors.state = { warningText: 'State is required' };
        if (!row.lineOfBusiness) errors.lineOfBusiness = { warningText: 'Line of Business is required' };
        if (!row.policyType) errors.policyType = { warningText: 'Policy Type is required' };
        if (toIdList(row.APlusCarrierToRate).length === 0) errors.APlusCarrierToRate = { warningText: 'A+ Carrier is required' };
        if (requireCancellationReason && toIdList(row.cancellationReason).length === 0) {
            errors.cancellationReason = { warningText: 'Cancellation Reason is required' };
        }
        if (requireRate && !hasValue(row.percentage) && !hasValue(row.dollar)) {
            errors.percentage = { warningText: 'Percentage or Dollar is required' };
            errors.dollar = { warningText: 'Percentage or Dollar is required' };
        }
        if (requireRate && hasValue(row.percentage) &&
            (!/^\d+(\.\d)?$/.test(String(row.percentage)) || Number(row.percentage) < 1 || Number(row.percentage) > 99.9)) {
            errors.percentage = { warningText: 'Must be 1.0-99.9 in increments of 0.1' };
        }
        if (requireRate && hasValue(row.dollar) &&
            (!/^\d+$/.test(String(row.dollar)) || Number(row.dollar) < 1 || Number(row.dollar) > 9999)) {
            errors.dollar = { warningText: 'Must be a whole number from 1-9999' };
        }
        return errors;
    };
}